'use client';

import { cn } from '@coas/ui/lib';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useId } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';

type PhoneInputProps<TFieldValues extends FieldValues = never> = UseControllerProps<TFieldValues> & {
    className?: string;
    label?: React.ReactNode;
};

export default <TFieldValues extends FieldValues = never>(props: PhoneInputProps<TFieldValues>) => {
    const { label, ...rest } = props;
    const { field, fieldState } = useController(rest);

    const id = useId();
    const [parent] = useAutoAnimate();

    return (
        <div className='space-y-1'>
            {label && (
                <label htmlFor={id} className='font-500 text-neutral-500'>
                    {label}
                </label>
            )}

            <div ref={parent} className='flex flex-col items-stretch'>
                <PhoneInput
                    country='ru'
                    disableDropdown
                    countryCodeEditable={false}
                    masks={{ ru: '(...) ...-..-..' }}
                    enableAreaCodes
                    disableCountryGuess
                    specialLabel=''
                    disableSearchIcon
                    value={field.value ?? ''}
                    onChange={val => field.onChange(val)}
                    onBlur={field.onBlur}
                    inputProps={{
                        id,
                        name: field.name,
                        autoComplete: 'tel',
                        ref: field.ref,
                    }}
                    containerClass='w-full'
                    inputClass={cn(
                        'w-full border-2 border-neutral-300 bg-transparent p-2 outline-none outline-2 focus:border-blue-600',
                        fieldState.invalid && 'border-red-600',
                        props.className,
                    )}
                />

                {fieldState.invalid && fieldState.error?.message && (
                    <span className='bg-red-200 p-2 font-400 text-14 text-red-500'>{fieldState.error.message}</span>
                )}
            </div>
        </div>
    );
};
